import axios from 'axios';
import { isEmpty, values } from 'lodash-es';
import {
  MF_PORTFOLIO_LANDING_STATUS_CODE,
  PORTFOLIO_LANDING_STATUS_CODES,
} from '../constants/portfolio';
import { handleApi } from './common';
import { COMMON } from '../strings/common';
export const genericErrorMessage = 'Something went wrong !';

const calcSipEndPoint = (isin, upcoming) => {
  let endPoint = '/api/portfolio/funds/sips';
  let params = '';
  if (isin) {
    params += `?isin=${isin}`;
  }
  if (upcoming) {
    let upcomingParams = params ? '&' : '?';
    params += `${upcomingParams}upcoming=${upcoming}`;
  }
  endPoint += params;
  return endPoint;
};

const calcRtaTransactionEndPoint = (isin, ttype, scheme_category, scheme_subcategory, from_tdate, to_tdate) => {
  let endPoint = '/api/portfolio/rta/transactions/v2';
  let params = '';
  if (isin) {
    params += `?isin=${isin}`;
  }
  if (ttype) {
    let paramAppened = params ? '&' : '?';
    params += `${paramAppened}ttype=${ttype}`;
  }
  if (scheme_category) {
    let paramAppened = params ? '&' : '?';
    params += `${paramAppened}scheme_category=${scheme_category}`;
  }
  if (scheme_subcategory) {
    let paramAppened = params ? '&' : '?';
    params += `${paramAppened}scheme_subcategory=${scheme_subcategory}`;
  }
  if (from_tdate) {
    let paramAppened = params ? '&' : '?';
    params += `${paramAppened}from_tdate=${from_tdate}`;
  }
  if (to_tdate) {
    let paramAppened = params ? '&' : '?';
    params += `${paramAppened}to_tdate=${to_tdate}`;
  }
  endPoint += params;
  return endPoint;
};

const PORTFOLIO_APIS = {
  portfolioSummary: '/api/portfolio/summary/landing/overall',
  mfSummary: '/api/portfolio/summary/mf/overall',
  availableSwitchFunds: (amfi) => `/api/invest/fund/switch/available-funds?amfi=${amfi}`,
  switchFundDetails: (amfi) => `api/invest/reportv4/portfolio/fund-detail?amfi=${amfi}`,
  switchOrders: '/api/invest/switch/orders',
  yourFunds: '/api/portfolio/funds/holdings',
  getSips: calcSipEndPoint,
  getRtaTransactions: calcRtaTransactionEndPoint,
  pendingTransactions: '/api/portfolio/orders/pending/v2',
  sipPauseCancel: '/api/invest/sip/',
  verifyEditSip: "/api/invest/sip/edit/check",
  confirmEditSip: "/api/invest/sip/edit/commit",
  externalPortfolioSummary: "/api/portfolio/external",
  getSwitchTaxes: "/api/invest/withdrawal/v2/get_taxes",
};

export const fetch_external_portfolio_summary = async (Api, token) => {
  const response = await Api.get(PORTFOLIO_APIS.externalPortfolioSummary, {}, {}, token);
  return handleApi(response);
};

export const fetch_portfolio_summary = async (Api, token) => {
  const response = await Api.get(PORTFOLIO_APIS.portfolioSummary, {}, {}, token);
  return handlePortfolioApi(response, 'portfolio');
};

export const fetch_mf_summary = async (Api, token) => {
  const response = await Api.get(PORTFOLIO_APIS.mfSummary, {}, {}, token);
  return handlePortfolioApi(response, 'mf');
};

export const fetch_available_switch_funds = async (Api, amfi, token) => {
  const endPoint = PORTFOLIO_APIS.availableSwitchFunds(amfi);
  const response = await Api.get(endPoint, {}, {}, token);
  return handleApi(response);
};

export const fetch_switch_fund_details = async (Api, amfi, token) => {
  const endPoint = PORTFOLIO_APIS.switchFundDetails(amfi);
  const response = await Api.get(endPoint, {}, {}, token);
  return handleApi(response);
};

export const place_switch_orders = async (Api, body = {}, token) => {
  const endPoint = PORTFOLIO_APIS.switchOrders;
  const response = await Api.post(endPoint, body, {}, token);
  return handleApi(response);
};

export const resend_switch_otp = async (Api, endPoint) => {
  const response = await Api.get(endPoint);
  return handleApi(response);
};

export const verify_switch_otp = async (Api, endPoint, otp) => {
  const url = `${endPoint}?otp=${otp}`;
  const response = await Api.post(url);
  return handleApi(response);
};

export const fetch_your_funds = async (Api, token) => {
  const endPoint = PORTFOLIO_APIS.yourFunds;
  const response = await Api.get(endPoint, {}, {}, token);
  return handleApi(response);
};

export const fetch_sips = async (Api, isin = '', isUpcoming = false, token) => {
  const endPoint = PORTFOLIO_APIS.getSips(isin, isUpcoming);
  const response = await Api.get(endPoint, {}, {}, token);
  return handleApi(response);
};

export const fetch_rta_transactions = async (
  Api,
  isin = '',
  ttype = '',
  scheme_category = '',
  scheme_subcategory = '',
  from_tdate,
  to_tdate,
  token
) => {
  const endPoint = PORTFOLIO_APIS.getRtaTransactions(isin, ttype, scheme_category, scheme_subcategory, from_tdate, to_tdate);
  const response = await Api.get(endPoint, {}, {}, token);
  return handleApi(response);
};

export const fetch_pending_transactions = async (Api, token) => {
  const response = await Api.get(
    PORTFOLIO_APIS.pendingTransactions,
    {},
    {},
    token
  );
  return handleApi(response);
};

export const fetch_more_transactions = async (Api, url, token) => {
  const response = await Api.get(url, {}, {}, token);
  return handleApi(response);
};

export const getSipAction = async (Api, data) => {
  const response = await Api.get(
    `${PORTFOLIO_APIS.sipPauseCancel}${data.key}/${data.action}`
    )
  return handleApi(response);
}

export const post_sip_action = async (Api, data) => {
  const tenor = data.action === "pause" ? { tenor: data.period } : {};
  const res = await Api.post(
    `${PORTFOLIO_APIS.sipPauseCancel}${data.key}/${data.action}`,
    tenor
  );
  return handleApi(res);
}

export const submit_sip_action_otp = async (Api, data) => {
  const res = await Api.post(data.url, {
    otp: data.otp,
  });
  return handleApi(res);
}

export const resendSipActionOtp = async (Api, data) => {
  const res = await Api.get(data.url);
  return handleApi(res);
}

export const verify_edit_sip = async (Api, data, token) => {
  const res = await Api.post(PORTFOLIO_APIS.verifyEditSip, data, {}, token);
  return handleApi(res);
}

export const confirm_edit_sip = async (Api, data, token) => {
  const res = await Api.post(PORTFOLIO_APIS.confirmEditSip, data, {}, token);
  return handleApi(res);
}

export const fetch_switch_taxes = async (Api, body = {}, token) => {
	const endPoint = PORTFOLIO_APIS.getSwitchTaxes;
	const response = await Api.post(endPoint, body, {}, token);
	return handleApi(response);
}

const getAllowedStatus = (screen) => {
  if (screen === 'mf') {
    const statusCodes = values(MF_PORTFOLIO_LANDING_STATUS_CODE);
    return statusCodes;
  } else if (screen === 'portfolio') {
    const statusCodes = values(PORTFOLIO_LANDING_STATUS_CODES);
    return statusCodes;
  }
};

const handlePortfolioApi = (res, screen) => {
  if (axios.isCancel(res)) {
    throw COMMON.ApiCancelled;
  }
  if (res.pfwstatus_code !== 200 || !res.pfwresponse || isEmpty(res.pfwresponse)) {
    throw {
      message: res.pfwmessage || genericErrorMessage,
      statusCode: res.pfwstatus_code,
    };
  }
  const { result, status_code: status } = res.pfwresponse;
  const allowedStatus = getAllowedStatus(screen);
  if (allowedStatus.includes(status) || status === 200) {
    return { result, status };
  } else {
    throw {
      message: result.error || result.message || genericErrorMessage,
      statusCode: res.status_code,
    };
  }
};
